import type { CurvaDocument } from "@/document/model/document";
import { normalizeStrokeStack, type Paint, type StrokeStyle } from "@/document/model/styles";
import { createId } from "@/lib/ids";

interface RasterAssetRecord {
  id: string;
  name?: string;
  mimeType?: string;
  dataUrl?: string;
  bytes?: Uint8Array;
}

const rasterAssets = new Map<string, RasterAssetRecord>();

export function registerRasterAssetFromDataUrl({
  id,
  dataUrl,
  name,
  mimeType,
}: {
  id?: string;
  dataUrl: string;
  name?: string;
  mimeType?: string;
}): string {
  const assetId = id ?? createId("asset");
  rasterAssets.set(assetId, {
    id: assetId,
    name,
    mimeType: mimeType || mimeTypeFromDataUrl(dataUrl),
    dataUrl,
    bytes: bytesFromDataUrl(dataUrl),
  });
  return assetId;
}

export function registerRasterAssetBytes({
  id,
  bytes,
  name,
  mimeType,
}: {
  id: string;
  bytes: Uint8Array;
  name?: string;
  mimeType?: string;
}): void {
  rasterAssets.set(id, {
    id,
    name,
    mimeType,
    bytes,
  });
}

export function rasterAssetDataUrl(assetId: string | undefined, fallbackSource?: string): string | undefined {
  if (!assetId) {
    return fallbackSource;
  }

  const asset = rasterAssets.get(assetId);
  if (!asset) {
    return fallbackSource;
  }

  if (!asset.dataUrl && asset.bytes) {
    asset.dataUrl = dataUrlFromBytes(asset.bytes, asset.mimeType ?? "image/png");
  }
  return asset.dataUrl ?? fallbackSource;
}

export function rasterAssetBytes(assetId: string | undefined, fallbackSource?: string): { bytes: Uint8Array; mimeType?: string } | undefined {
  if (!assetId) {
    return fallbackSource ? { bytes: bytesFromDataUrl(fallbackSource), mimeType: mimeTypeFromDataUrl(fallbackSource) } : undefined;
  }

  const asset = rasterAssets.get(assetId);
  if (asset?.bytes) {
    return { bytes: asset.bytes, mimeType: asset.mimeType };
  }

  if (asset?.dataUrl) {
    asset.bytes = bytesFromDataUrl(asset.dataUrl);
    return { bytes: asset.bytes, mimeType: asset.mimeType ?? mimeTypeFromDataUrl(asset.dataUrl) };
  }

  return fallbackSource ? { bytes: bytesFromDataUrl(fallbackSource), mimeType: mimeTypeFromDataUrl(fallbackSource) } : undefined;
}

export function imagePaintsInDocument(document: CurvaDocument): Array<Extract<Paint, { type: "image" }>> {
  const paints: Array<Extract<Paint, { type: "image" }>> = [];
  for (const page of document.pages) {
    for (const node of page.children) {
      collectImagePaintsFromNode(node, paints);
    }
  }
  return paints;
}

export function documentWithoutEmbeddedImageSources(document: CurvaDocument): CurvaDocument {
  return {
    ...document,
    pages: document.pages.map(page => ({
      ...page,
      children: page.children.map(stripNodeImageSources),
    })),
  };
}

export function documentWithExternalizedImageSources(document: CurvaDocument): CurvaDocument {
  return {
    ...document,
    pages: document.pages.map(page => ({
      ...page,
      children: page.children.map(externalizeNodeImageSources),
    })),
  };
}

function collectImagePaintsFromNode(node: CurvaDocument["pages"][number]["children"][number], paints: Array<Extract<Paint, { type: "image" }>>): void {
  for (const paint of node.style.fills) {
    if (paint.type === "image") {
      paints.push(paint);
    }
  }
  for (const stroke of normalizeStrokeStack(node.style)) {
    if (stroke.paint.type === "image") {
      paints.push(stroke.paint);
    }
  }
  if (node.type === "frame" || node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    for (const child of node.children) {
      collectImagePaintsFromNode(child, paints);
    }
  }
  if (node.type === "boolean") {
    for (const child of node.sourceNodes) {
      collectImagePaintsFromNode(child, paints);
    }
  }
}

function stripNodeImageSources<T extends CurvaDocument["pages"][number]["children"][number]>(node: T): T {
  const next = {
    ...node,
    style: {
      ...node.style,
      fills: node.style.fills.map(stripPaintSource),
      ...strokeStackFields(normalizeStrokeStack(node.style).map(stroke => ({ ...stroke, paint: stripPaintSource(stroke.paint) }))),
    },
  } as T;

  if (next.type === "frame" || next.type === "group" || next.type === "component" || next.type === "componentVariant" || next.type === "instance") {
    next.children = next.children.map(stripNodeImageSources);
  }
  if (next.type === "boolean") {
    next.sourceNodes = next.sourceNodes.map(stripNodeImageSources);
  }
  return next;
}

function stripPaintSource(paint: Paint): Paint {
  if (paint.type !== "image" || !paint.source) {
    return paint;
  }
  const { source: _source, ...rest } = paint;
  return rest;
}

function externalizeNodeImageSources<T extends CurvaDocument["pages"][number]["children"][number]>(node: T): T {
  const next = {
    ...node,
    style: {
      ...node.style,
      fills: node.style.fills.map(externalizePaintSource),
      ...strokeStackFields(normalizeStrokeStack(node.style).map(stroke => ({ ...stroke, paint: externalizePaintSource(stroke.paint) }))),
    },
  } as T;

  if (next.type === "frame" || next.type === "group" || next.type === "component" || next.type === "componentVariant" || next.type === "instance") {
    next.children = next.children.map(externalizeNodeImageSources);
  }
  if (next.type === "boolean") {
    next.sourceNodes = next.sourceNodes.map(externalizeNodeImageSources);
  }
  return next;
}

function strokeStackFields(strokes: StrokeStyle[]): { strokes: StrokeStyle[]; stroke?: StrokeStyle } {
  return {
    strokes,
    stroke: strokes[0],
  };
}

function externalizePaintSource(paint: Paint): Paint {
  if (paint.type !== "image" || !paint.source) {
    return paint;
  }

  const assetId = paint.assetId ?? registerRasterAssetFromDataUrl({
    dataUrl: paint.source,
    name: paint.name,
    mimeType: paint.mimeType,
  });
  const { source: _source, ...rest } = paint;
  return {
    ...rest,
    assetId,
    mimeType: paint.mimeType ?? mimeTypeFromDataUrl(paint.source),
  };
}

function mimeTypeFromDataUrl(dataUrl: string): string | undefined {
  const match = /^data:([^;,]+)[;,]/.exec(dataUrl);
  return match?.[1];
}

function bytesFromDataUrl(dataUrl: string): Uint8Array {
  const commaIndex = dataUrl.indexOf(",");
  if (commaIndex === -1) {
    return new Uint8Array();
  }
  const header = dataUrl.slice(0, commaIndex);
  const payload = dataUrl.slice(commaIndex + 1);
  if (header.includes(";base64")) {
    const binary = atob(payload);
    return Uint8Array.from(binary, character => character.charCodeAt(0));
  }
  return new TextEncoder().encode(decodeURIComponent(payload));
}

function dataUrlFromBytes(bytes: Uint8Array, mimeType: string): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return `data:${mimeType};base64,${btoa(binary)}`;
}
